import React, { useState } from "react";
import {
  Cloud,
  Database,
  Lock,
  Server,
  Key,
  Code,
  Cpu,
  ArrowRight,
  BookOpen,
  Github,
  ExternalLink,
  Menu,
  X,
} from "lucide-react";

const ProtocolLanding = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigation = [
    { name: "Home", href: "/" },
    { name: "Protocol", href: "/protocol", current: true },
    { name: "Labs", href: "/labs" },
    { name: "Docs", href: "https://docs.tinycloud.xyz", external: true },
    {
      name: "GitHub",
      href: "https://github.com/tinycloudlabs",
      external: true,
    },
  ];

  const tableOfContents = [
    { name: "Overview", href: "#overview" },
    { name: "Key Components", href: "#components" },
    { name: "Architecture", href: "#architecture" },
    { name: "Use Cases", href: "#use-cases" },
    { name: "Security", href: "#security" },
    { name: "Getting Started", href: "#getting-started" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header */}
      <nav className="border-b border-[#4473b9]/30 bg-[#4473b9] backdrop-blur-md sticky top-0 z-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 justify-between">
            <div className="flex">
              <div className="flex flex-shrink-0 items-center">
                <a href="/" className="flex items-center">
                  <img
                    src="/tinycloud-text-logo.png"
                    alt="TinyCloud"
                    className="h-8 w-auto object-contain"
                  />
                </a>
              </div>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                    item.current
                      ? "border-b-2 border-white text-white"
                      : "text-white/90 hover:text-white hover:border-b-2 hover:border-white/50"
                  }`}
                >
                  {item.name}
                  {item.external && <ExternalLink className="ml-1 h-3 w-3" />}
                </a>
              ))}
            </div>
            <div className="sm:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md p-2 text-white/90 hover:bg-white/10 hover:text-white"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden">
            <div className="space-y-1 pb-3 pt-2">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                  className={`block py-2 pl-3 pr-4 text-base font-medium ${
                    item.current
                      ? "bg-white/20 text-white"
                      : "text-white/90 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {item.name}
                  {item.external && (
                    <ExternalLink className="ml-1 h-3 w-3 inline" />
                  )}
                </a>
              ))}
            </div>
          </div>
        )}
      </nav>

      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Table of Contents - Desktop */}
          <aside className="hidden lg:col-span-3 lg:block">
            <div className="sticky top-24">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Contents
              </h3>
              <nav className="mt-4">
                <ul className="space-y-2">
                  {tableOfContents.map((item) => (
                    <li key={item.name}>
                      <a
                        href={item.href}
                        className="block text-sm text-gray-600 hover:text-gray-900 transition-colors"
                      >
                        {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-9">
            <article className="prose prose-lg max-w-none">
              {/* Hero Section */}
              <div id="overview" className="mb-16">
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  TinyCloud Protocol
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed mb-8">
                  The decentralized foundation for sovereign data. TinyCloud
                  Protocol provides a user-controlled "cloud" that serves as the
                  backend for multiple apps, enabling data sovereignty and
                  privacy by design.
                </p>

                <div className="flex flex-wrap gap-4 mb-12">
                  <a
                    href="https://demo.tinycloud.xyz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-[#4473b9] text-white font-semibold rounded-lg hover:bg-[#4473b9/90] transition-colors"
                  >
                    Try Demo
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                  <a
                    href="https://docs.tinycloud.xyz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Code className="mr-2 h-4 w-4" />
                    View Docs
                  </a>
                  <a
                    href="https://github.com/tinycloudlabs/tinycloud-node"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Github className="mr-2 h-4 w-4" />
                    GitHub
                  </a>
                </div>
              </div>

              {/* Key Components */}
              <section id="components" className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Key Components
                </h2>
                <div className="grid md:grid-cols-2 gap-8">
                  {[
                    {
                      icon: Database,
                      title: "Key-Value Store & Buckets",
                      description:
                        "Simple, flexible storage similar to an S3-like interface for all your data needs. Organize data into logical buckets with fine-grained access control.",
                    },
                    {
                      icon: Cpu,
                      title: "Compute Layer",
                      description:
                        "Run functions or queries on your data with privacy-preserving computation. Execute code without exposing raw data to external services.",
                    },
                    {
                      icon: Key,
                      title: "Encryption & Capabilities",
                      description:
                        "Fine-grained permission tokens and data-logic for secure access control. Cryptographic keys ensure only authorized access to your data.",
                    },
                    {
                      icon: Server,
                      title: "Decentralized Architecture",
                      description:
                        "User-controlled infrastructure that resists centralized control. Run your own node or choose from trusted providers.",
                    },
                  ].map((component) => (
                    <div
                      key={component.title}
                      className="border border-gray-200 rounded-lg p-6 hover:border-[#4473b9] transition-colors"
                    >
                      <div className="flex items-center mb-4">
                        <div className="p-2 bg-[#4473b9]/10 rounded-lg mr-4">
                          <component.icon className="h-6 w-6 text-[#4473b9]" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900">
                          {component.title}
                        </h3>
                      </div>
                      <p className="text-gray-600 leading-relaxed">
                        {component.description}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Architecture */}
              <section id="architecture" className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Architecture
                </h2>
                <div className="bg-gray-50 rounded-lg p-8 mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Core Design Principles
                  </h3>
                  <ul className="space-y-4 text-gray-600">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-[#4473b9] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <strong>User Sovereignty:</strong> Users maintain
                        complete control over their data and computation
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-[#4473b9] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <strong>Privacy by Design:</strong> Data is encrypted at
                        rest and in transit with user-controlled keys
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-[#4473b9] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <strong>Interoperability:</strong> Multiple applications
                        can safely access and process user data
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-[#4473b9] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <strong>Decentralization:</strong> No single point of
                        failure or control in the system
                      </div>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Use Cases */}
              <section id="use-cases" className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Use Cases
                </h2>
                <div className="space-y-6">
                  <div className="border-l-4 border-[#4473b9] pl-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Location Streaming
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      An app that continuously streams a user's location into
                      their TinyCloud node, giving you complete control over
                      your location data. Share location selectively with
                      friends or services without giving up sovereignty.
                    </p>
                  </div>
                  <div className="border-l-4 border-[#4473b9] pl-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Privacy-Preserving Social
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      A 'Find My Friends' clone that lets you see if friends are
                      nearby or in a particular city without exposing real-time
                      location data to third-party servers. Compute proximity
                      without sharing exact coordinates.
                    </p>
                  </div>
                  <div className="border-l-4 border-[#4473b9] pl-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      AI & Knowledge Management
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Store transcripts, notes, and knowledge graphs in your
                      personal cloud. AI assistants can process and learn from
                      your data while keeping everything under your control.
                    </p>
                  </div>
                </div>
              </section>

              {/* Security */}
              <section id="security" className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Security & Privacy
                </h2>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-green-900 mb-3">
                      Encryption
                    </h3>
                    <ul className="space-y-2 text-green-800 text-sm">
                      <li>• End-to-end encryption with user-controlled keys</li>
                      <li>• XChaCha20-Poly1305 encryption at rest</li>
                      <li>• Post-quantum cryptography compatibility</li>
                    </ul>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-blue-900 mb-3">
                      Access Control
                    </h3>
                    <ul className="space-y-2 text-blue-800 text-sm">
                      <li>• Capabilities-based security model</li>
                      <li>• Ethereum-compatible signing (ECDSA/BLS)</li>
                      <li>• Revocable and time-limited permissions</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Getting Started */}
              <section id="getting-started" className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Getting Started
                </h2>
                <div className="bg-gray-50 rounded-lg p-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Quick Start
                  </h3>
                  <div className="bg-white rounded border p-4 mb-6">
                    <pre className="text-sm text-gray-800 overflow-x-auto">
                      <code>{`const tc = new TinyCloud(keypair);

// Store data
await tc.put('my-notes/2024', noteContent, {
  read: ['my-ai-assistant.app'],
  write: ['note-taking.app']
});

// Retrieve data
const notes = await tc.get('my-notes/2024');`}</code>
                    </pre>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <span className="flex items-center justify-center w-6 h-6 bg-[#4473b9] text-white text-sm font-bold rounded-full mr-3">
                        1
                      </span>
                      <span className="text-gray-700">
                        <a
                          href="https://docs.tinycloud.xyz"
                          className="text-[#4473b9] hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Read the documentation
                        </a>{" "}
                        to understand the core concepts
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="flex items-center justify-center w-6 h-6 bg-[#4473b9] text-white text-sm font-bold rounded-full mr-3">
                        2
                      </span>
                      <span className="text-gray-700">
                        Try the{" "}
                        <a
                          href="https://demo.tinycloud.xyz"
                          className="text-[#4473b9] hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          interactive demo
                        </a>{" "}
                        to see it in action
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="flex items-center justify-center w-6 h-6 bg-[#4473b9] text-white text-sm font-bold rounded-full mr-3">
                        3
                      </span>
                      <span className="text-gray-700">
                        Check out the{" "}
                        <a
                          href="https://github.com/tinycloudlabs/tinycloud-node"
                          className="text-[#4473b9] hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          GitHub repository
                        </a>{" "}
                        for code examples
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Footer Links */}
              <div className="border-t border-gray-200 pt-8">
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <div className="space-x-4">
                    <a
                      href="/"
                      className="hover:text-gray-700 transition-colors"
                    >
                      Home
                    </a>
                    <a
                      href="/labs"
                      className="hover:text-gray-700 transition-colors"
                    >
                      Labs
                    </a>
                    <a
                      href="/litepaper"
                      className="hover:text-gray-700 transition-colors"
                    >
                      Litepaper
                    </a>
                  </div>
                  <div className="space-x-4">
                    <a
                      href="/privacy"
                      className="hover:text-gray-700 transition-colors"
                    >
                      Privacy
                    </a>
                    <a
                      href="https://github.com/tinycloudlabs"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-gray-700 transition-colors"
                    >
                      GitHub
                    </a>
                  </div>
                </div>
              </div>
            </article>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProtocolLanding;
